/**
 * Icon picker over the whole Lucide library.
 *
 * Lucide is ISC licensed and already a production dependency on collctiv.com, so these cost
 * no new assets. 1,539 icons is far too many to scroll, so the picker is search-first with
 * the Collctiv-relevant ones offered up front.
 */

import { useMemo, useState } from 'react';
import type { CustomFieldRender } from '@puckeditor/core';
import { iconNode, searchIcons, usePreloadedIcons } from '../icons.js';

export const IconField: CustomFieldRender<string> = ({ value, onChange, readOnly }) => {
  const [query, setQuery] = useState('');
  const results = useMemo(() => searchIcons(query, 96), [query]);

  // Fetch the visible results together so the grid fills in one go rather than icon by icon.
  usePreloadedIcons(results);

  return (
    <div className="lpf">
      <div className="lpf__icon-current">
        <span className="lpf__icon-preview">{iconNode(value || 'gift', 20)}</span>
        <code className="lpf__icon-name">{value || 'gift'}</code>
      </div>

      <input
        className="lpf__input"
        type="search"
        value={query}
        disabled={readOnly}
        placeholder="Search 1,539 icons — try “gift”, “users”, “calendar”"
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="lpf__icons" role="radiogroup" aria-label="Icon">
        {results.map((name) => (
          <button
            key={name}
            type="button"
            role="radio"
            aria-checked={value === name}
            aria-label={name}
            title={name}
            disabled={readOnly}
            className={`lpf__icon${value === name ? ' lpf__icon--on' : ''}`}
            onClick={() => onChange(name)}
          >
            {iconNode(name, 18)}
          </button>
        ))}
      </div>

      {results.length === 0 && (
        <span className="lpf__hint">No icon matches “{query}”.</span>
      )}
    </div>
  );
};
